import React, { useEffect, Fragment, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '../../Services/translation';
import { numberFormat } from '../../Services/formatting';
import convertUnit from '../../Services/conversions';
import { map } from '../../Services/utilities';
import PanelBeam from './PanelBeam';
import PanelMarking from './PanelMarking';
import { validateInputs } from './validateCuttingInputs';
import { googleAnalyticsProductConfigurator } from '../../Services/googleAnalytics';
import { setEmptyCuttingDetails as addCutting, setCuttingDetails, deleteCuttingDetails as deleteCutting, } from '../../Actions/Configurator.action';

import PlusIcon from '../../../images/Plus.svg';
import TrashCan from '../../../images/trashcan.svg';

const PanelCuttingLength = ({ quantityRef, lengthRef }) => {
  const kgSuffix = ' kg';
  const configurator = useSelector((state) => state.configurator);
  const { rules, selections, quantitySelector } = configurator;
  const { baseUnit, quantityUnits } = quantitySelector;

  const cuttingElementRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (selections.cuttingDetails && selections.cuttingDetails.length === 0) {
      cuttingAdd();
    }
  }, []);

  useEffect(() => {
    const handleClick = event => {
      if (cuttingElementRef.current > 0 && !event.target.closest('details')) {
        cuttingElementRef.current.querySelectorAll('details').forEach((detail) => detail.removeAttribute('open'));
      };
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, []);

  const handleInputChangeOrBlur = (id, field, event, isBlur = false) => {
    let { value, min, max } = event.target;

    event.target.setCustomValidity('');

    if (value === '' && !isBlur) {
      dispatch(setCuttingDetails(id, field, value));
      return;
    }

    if (value === '' && isBlur && min !== null) {
      value = min;
    } else if (isBlur) {
      value = Math.max(Number(min), Math.min(Number(max), Number(value)));
    }
    dispatch(setCuttingDetails(id, field, value));

    if (isBlur) {
      googleAnalyticsProductConfigurator(field === 'quantity' ? 'cuttingNumber' : 'cuttingLength', value);
    }
  };

  const cuttingAdd = () => {
    if (validateInputs(quantityRef, lengthRef)) {
      dispatch(addCutting());
    }
  };

  const handleDelete = (id) => {
    if (selections.cuttingDetails.length === 1) {
      dispatch(deleteCutting(id));
      dispatch(addCutting());
    } else {
      dispatch(deleteCutting(id));
    }
  };

  const calculateWeight = (row) => {
    const pcsUnit = quantityUnits.find((u) => u.unit === 'pcs'),
      kgUnit = quantityUnits.find((u) => u.unit === 'kg');

    let baseQuantity = convertUnit(row.quantity, pcsUnit, baseUnit),
      totalWeight = convertUnit(baseQuantity, baseUnit, kgUnit);

    if (isCutLengthValid(row.length)) {
      totalWeight = map(row.length, 0, rules.length, 0, totalWeight);
    }

    return totalWeight;
  };

  const isCutLengthValid = (length) => {
    return length >= rules.minCutLength && length <= rules.maxCutLength;
  };

  const getRowWeight = (accumulator, row) => {
    return accumulator + calculateWeight(row);
  };

  const getTotalWeight = () => {
    return selections.cuttingDetails.reduce(getRowWeight, 0);
  };

  return (
    <Fragment>
      <div className='configurator-box' ref={cuttingElementRef}>
        <section className='header-section'>
          <span>{translate('cutting.cuttinglengthtitle')}</span>
          <details name='group1' className='config-details config-details--right-align config-details--link'>
            <summary>
              <span>{translate('cutting.showtoleranser')}</span>
              <img className='config-details__icon' src='/ui/images/arrow-down.svg'
              />
            </summary>
            <div className='config-details__content'>
              <img src={rules.toleranceImage} style={{ minWidth: '400px' }} />
            </div>
          </details>
        </section>

        <div className='cut-table'>
          <table>
            <thead>
              <tr>
                <th className='cut-table__quantity-cell'>
                  {translate('cutting.cuttinginformation.pieces')}
                </th>
                <th className='cut-table__length-cell'>
                  {translate('cutting.cuttinginformation.length')}
                </th>
                <th className='cut-table__cut-cell'>
                  {translate('cutting.cuttinginformation.capeType')}
                </th>
                <th className='cut-table__marking-cell'>
                  {translate('cutting.cuttinginformation.marking')}
                </th>
                <th className='cut-table__weight-cell'>
                  {translate('cutting.cuttinginformation.rowweight')}
                </th>
                <th className='cut-table__delete-cell'>
                  {/* Delete column */}
                </th>
              </tr>
            </thead>
            <tbody>
              {selections.cuttingDetails && selections.cuttingDetails.map((row, index) => (
                <tr key={row.id}>
                  <td className='cut-table__quantity-cell'>
                    <input ref={(el) => (quantityRef.current[index] = el)} className='cut-table__input' type='number' min='1' max='99999999'
                      onChange={(event) => handleInputChangeOrBlur(row.id, 'quantity', event)}
                      onBlur={(event) => handleInputChangeOrBlur(row.id, 'quantity', event, true)} value={row.quantity}
                    />
                  </td>
                  <td>
                    <input ref={(el) => (lengthRef.current[index] = el)} className='cut-table__input' min={rules.minCutLength} max={rules.maxCutLength}
                      type='number' value={row.length === 0 ? '' : row.length.toString()} placeholder={`${rules.minCutLength}-${rules.maxCutLength}`}
                      onChange={(event) => handleInputChangeOrBlur(row.id, 'length', event)}
                      onBlur={(event) => handleInputChangeOrBlur(row.id, 'length', event, true)}
                    />
                  </td>
                  <td className='cut-table__cut-cell'>
                    <details className='config-details config-details--bordered' name='group1'                      >
                      <summary title={row.cutType.description}>
                        <span>
                          {row.cutType.description ? row.cutType.description : translate('not.selected')}
                        </span>
                        <img className='config-details__icon' src='/ui/images/arrow-down.svg' />
                      </summary>
                      <div className='config-details__content'>
                        <PanelBeam
                          id={row.id}
                          currentCutType={row.cutType}
                        ></PanelBeam>
                      </div>
                    </details>
                  </td>
                  <td className='cut-table__marking-cell'>
                    <details className='config-details config-details--bordered config-details--right-align' name='group1'                      >
                      <summary title={row.marking.description}>
                        <span>
                          {row.marking.description ? row.marking.description : translate('not.selected')}
                        </span>
                        <img className='config-details__icon' src='/ui/images/arrow-down.svg' />
                      </summary>
                      <div className='config-details__content'>
                        <PanelMarking
                          id={row.id}
                          IsPopup={true}
                        ></PanelMarking>
                      </div>
                    </details>
                  </td>
                  <td className='cut-table__weight-cell'>
                    {numberFormat.format(getRowWeight(0, row)) + kgSuffix}
                  </td>
                  <td className='cut-table__delete-cell'>
                    <a tabIndex={0} onClick={() => handleDelete(row.id)}>
                      <img src={TrashCan} alt='Delete' />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>
                  <a tabIndex={0} onClick={cuttingAdd}>
                    <img src={PlusIcon} alt='Add' />{' '}
                    {translate('cutting.cuttinginformation.add')}
                  </a>
                </td>
                <td colSpan={3} className='cut-table__total'>
                  {translate('cutting.cuttinginformation.totalweight')}:{' ' + numberFormat.format(getTotalWeight()) + kgSuffix}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Fragment>
  );
};

export default PanelCuttingLength;
